/**
 * Utilitaires pour l'export des transactions
 * 
 * Ce fichier contient toutes les fonctions de transformation, validation et formatage
 * utilisées pour exporter les transactions en PDF et Excel.
 * 
 * @module exportUtils
 */

import { toast } from 'react-toastify';

/**
 * Extrait et valide la date d'une transaction
 * Priorité : date (date réelle de la transaction) > created_at (date de création)
 * Ne jamais utiliser updated_at pour éviter les incohérences
 * 
 * @param {Object} transaction - La transaction à traiter
 * @returns {Date|null} La date valide ou null si aucune date valide trouvée
 */
export const getTransactionDate = (transaction) => {
    // Priorité 1 : date de la transaction (champ principal)
    const primaryDate = transaction.date || transaction.created_at;

    if (!primaryDate) {
        console.error(`[Export] Transaction ${transaction.id} sans date valide`);
        return null;
    }

    const dateObj = new Date(primaryDate);
    if (isNaN(dateObj.getTime())) {
        console.error(`[Export] Date invalide pour transaction ${transaction.id}: ${primaryDate}`);
        return null;
    }

    return dateObj;
};

/**
 * Extrait le nom du partenaire (client ou fournisseur) selon le type de transaction
 * Ne jamais utiliser libelle ou motif comme partenaire (ce ne sont pas des entités)
 * 
 * @param {Object} transaction - La transaction à traiter
 * @returns {string|null} Le nom du partenaire ou null si non trouvé
 */
export const getPartnerName = (transaction) => {
    // Priorité basée sur le type de transaction
    if (transaction.type === 'sortie') {
        // Pour les sorties (dépenses), chercher le fournisseur
        return transaction.fournisseurs_details?.nom
            || transaction.fournisseur_nom
            || transaction.fournisseurs
            || null;
    } else if (transaction.type === 'entree') {
        // Pour les entrées (recettes), chercher le client
        return transaction.client_details?.nom
            || transaction.client_nom
            || null;
    } else if (transaction.type === 'rapprochement') {
        // Pour les rapprochements, essayer les deux
        return transaction.fournisseurs_details?.nom
            || transaction.fournisseur_nom
            || transaction.fournisseurs
            || transaction.client_details?.nom
            || transaction.client_nom
            || null;
    }

    return null;
};

/**
 * Extrait et valide le montant d'une transaction
 * 
 * @param {Object} transaction - La transaction à traiter
 * @returns {number|null} Le montant validé ou null si invalide
 */
export const getTransactionAmount = (transaction) => {
    // Essayer montant_total en priorité, puis montant
    let rawAmount = transaction.montant_total !== undefined && transaction.montant_total !== null
        ? transaction.montant_total
        : transaction.montant;

    // Si c'est une string, nettoyer avant parsing
    if (typeof rawAmount === 'string') {
        rawAmount = rawAmount.replace(/[^0-9.-]+/g, '');
    }

    const amount = parseFloat(rawAmount);

    if (isNaN(amount)) {
        console.warn(`[Export] Montant invalide pour transaction ${transaction.id}: ${transaction.montant_total || transaction.montant}`);
        return null;
    }

    // Arrondir à 2 décimales pour FCFA
    return Math.round(amount * 100) / 100;
};

/**
 * Valide une transaction avant export
 * 
 * @param {Object} transaction - La transaction à valider
 * @returns {Object} { isValid: boolean, errors: string[], warnings: string[] }
 */
export const validateTransactionForExport = (transaction) => {
    const errors = [];
    const warnings = [];

    // Validation date
    const date = getTransactionDate(transaction);
    if (!date) {
        errors.push('Date invalide ou manquante');
    }

    // Validation montant
    const amount = getTransactionAmount(transaction);
    if (amount === null) {
        errors.push('Montant invalide');
    } else if (amount === 0) {
        warnings.push('Montant égal à zéro');
    } else if (amount < 0) {
        warnings.push('Montant négatif');
    }

    // Validation type
    if (!transaction.type || !['entree', 'sortie', 'rapprochement'].includes(transaction.type)) {
        errors.push('Type de transaction invalide');
    }

    // Warning si pas de partenaire
    const partner = getPartnerName(transaction);
    if (!partner && transaction.type !== 'rapprochement') {
        warnings.push('Partenaire non renseigné');
    }

    // Warning si pas de libellé
    if (!transaction.libelle && !transaction.motif && !transaction.intitule) {
        warnings.push('Libellé manquant');
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
};

/**
 * Formate une date au format ISO 8601 pour Excel
 * Format : YYYY-MM-DD
 * 
 * @param {Date} date - La date à formater
 * @returns {string} Date formatée
 */
export const formatDateForExcel = (date) => {
    if (!date || isNaN(date.getTime())) return '-';

    // Format ISO 8601 : YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Formate une date au format français lisible pour PDF
 * Format : DD/MM/YYYY
 * 
 * @param {Date} date - La date à formater
 * @returns {string} Date formatée
 */
export const formatDateForPDF = (date) => {
    if (!date || isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Formate un montant pour l'affichage (sans séparateurs ambigus)
 * Utilise des espaces comme séparateurs de milliers
 * 
 * @param {number} amount - Le montant à formater
 * @param {boolean} includeCurrency - Inclure "FCFA" dans le résultat
 * @returns {string} Montant formaté
 */
export const formatAmount = (amount, includeCurrency = true) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
        return includeCurrency ? '0 FCFA' : '0';
    }

    const num = parseFloat(amount);

    // Arrondir à l'entier le plus proche pour FCFA (pas de centimes)
    const rounded = Math.round(num);

    // Formater avec espaces comme séparateurs de milliers
    const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return includeCurrency ? `${formatted} FCFA` : formatted;
};

/**
 * Sanitize une valeur pour éviter l'injection de formules dans Excel
 * Préfixe les valeurs commençant par =, @, +, -, ou tab
 * 
 * @param {*} value - La valeur à nettoyer
 * @returns {*} Valeur nettoyée
 */
export const sanitizeExcelValue = (value) => {
    if (typeof value !== 'string') return value;

    // Détecter les formules potentielles
    if (/^[@=+\-\t\r]/.test(value)) {
        return "'" + value; // Préfixer avec apostrophe pour forcer Excel à traiter comme texte
    }

    return value;
};

/**
 * Génère un nom de fichier informatif et unique pour l'export
 * 
 * @param {Object} dossier - Les informations du dossier (peut être juste un ID)
 * @param {Object} filters - Les filtres appliqués
 * @param {string} format - 'xlsx' ou 'pdf'
 * @param {number} transactionCount - Nombre de transactions exportées
 * @returns {string} Nom de fichier
 */
export const generateFileName = (dossier, filters, format, transactionCount = 0) => {
    const parts = [];

    // Préfixe
    parts.push('Transactions');

    // Nom du dossier (nettoyer pour éviter les caractères spéciaux)
    const dossierName = typeof dossier === 'object'
        ? (dossier.nom || dossier.raison_sociale || `Dossier_${dossier.id || 'Inconnu'}`)
        : dossier;

    parts.push(sanitizeFileName(dossierName));

    // Période si spécifiée
    if (filters?.dateStart && filters?.dateEnd) {
        parts.push(`${filters.dateStart}_au_${filters.dateEnd}`);
    } else if (filters?.dateStart) {
        parts.push(`depuis_${filters.dateStart}`);
    } else if (filters?.dateEnd) {
        parts.push(`jusqua_${filters.dateEnd}`);
    }

    // Type de transaction si filtré
    if (filters?.type && filters.type !== 'all') {
        const typeMap = {
            'entree': 'Entrees',
            'sortie': 'Sorties',
            'rapprochement': 'Rapprochements'
        };
        parts.push(typeMap[filters.type] || filters.type);
    }

    // Nombre de transactions
    if (transactionCount > 0) {
        parts.push(`${transactionCount}tx`);
    }

    // Timestamp pour unicité (format : YYYYMMDD_HHMM)
    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
    ].join('') + '_' + [
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0')
    ].join('');
    parts.push(timestamp);

    // Joindre et ajouter l'extension
    return `${parts.join('_')}.${format}`;
};

/**
 * Nettoie un nom de fichier en retirant les caractères spéciaux
 * 
 * @param {string} name - Le nom à nettoyer
 * @returns {string} Nom nettoyé
 */
const sanitizeFileName = (name) => {
    return name
        .replace(/[^a-zA-Z0-9_\- ]/g, '') // Retirer caractères spéciaux
        .replace(/\s+/g, '_') // Remplacer espaces par underscores
        .slice(0, 50); // Limiter la longueur
};

/**
 * Formate le type de transaction pour l'affichage
 * 
 * @param {string} type - Le type de transaction
 * @returns {string} Type formaté
 */
export const formatTransactionType = (type) => {
    const typeMap = {
        'entree': 'Entrée',
        'sortie': 'Sortie',
        'rapprochement': 'Rapprochement'
    };
    return typeMap[type] || 'Non défini';
};

/**
 * Formate le mode de paiement pour l'affichage
 * 
 * @param {string} source - La source de paiement
 * @returns {string} Mode formaté
 */
export const formatPaymentMode = (source) => {
    const modeMap = {
        'caisse': 'Caisse',
        'banque': 'Banque',
        'wave': 'Wave',
        'orange_money': 'Orange Money'
    };
    return modeMap[source] || source || '-';
};

/**
 * Transforme une transaction en objet pour export Excel
 * MODIFIÉ : 8 colonnes (retiré N° Transaction, Devise, Notes)
 * 
 * @param {Object} transaction - La transaction à transformer
 * @returns {Object} Objet formaté pour Excel
 */
export const transformTransactionForExcel = (transaction) => {
    const date = getTransactionDate(transaction);
    const partner = getPartnerName(transaction);
    const amount = getTransactionAmount(transaction);

    return {
        'Date': formatDateForExcel(date),
        'Partenaire': sanitizeExcelValue(partner || 'Non renseigné'),
        'Type': sanitizeExcelValue(formatTransactionType(transaction.type)),
        'Libellé': sanitizeExcelValue(transaction.libelle || transaction.motif || transaction.intitule || '-'),
        'Montant': amount,
        'Mode de paiement': sanitizeExcelValue(formatPaymentMode(transaction.source_paiement)),
        'Statut': sanitizeExcelValue(transaction.status || 'Non défini'),
        'Date de création': formatDateForExcel(transaction.created_at ? new Date(transaction.created_at) : null)
    };
};

/**
 * Transforme une transaction en tableau pour export PDF
 * 
 * @ param {Object} transaction - La transaction à transformer
 * @returns {Array} Tableau de valeurs pour le PDF
 */
export const transformTransactionForPDF = (transaction) => {
    const date = getTransactionDate(transaction);
    const partner = getPartnerName(transaction);
    const amount = getTransactionAmount(transaction);

    return [
        formatDateForPDF(date),
        partner || 'Non renseigné',
        formatTransactionType(transaction.type),
        transaction.libelle || transaction.motif || transaction.intitule || '-',
        formatAmount(amount),
        formatPaymentMode(transaction.source_paiement)
    ];
};

/**
 * Valide un lot de transactions et retourne les statistiques
 * 
 * @param {Array} transactions - Les transactions à valider
 * @returns {Object} Statistiques de validation
 */
export const validateTransactionBatch = (transactions) => {
    const stats = {
        total: transactions.length,
        valid: 0,
        invalid: 0,
        withWarnings: 0,
        errors: [],
        warnings: []
    };

    transactions.forEach((transaction, index) => {
        const validation = validateTransactionForExport(transaction);

        if (validation.isValid) {
            stats.valid++;
        } else {
            stats.invalid++;
            stats.errors.push({
                index,
                id: transaction.id,
                errors: validation.errors
            });
        }

        if (validation.warnings.length > 0) {
            stats.withWarnings++;
            stats.warnings.push({
                index,
                id: transaction.id,
                warnings: validation.warnings
            });
        }
    });

    return stats;
};

/**
 * Affiche les résultats de validation à l'utilisateur
 * 
 * @param {Object} validationStats - Les statistiques de validation
 * @returns {boolean} true si l'utilisateur confirme l'export, false sinon
 */
export const showValidationResults = (validationStats) => {
    const { total, valid, invalid, withWarnings, errors, warnings } = validationStats;

    // Aucune erreur, juste des warnings
    if (invalid === 0 && withWarnings > 0) {
        const warningMsg = `${withWarnings} transaction(s) avec avertissements sur ${total}.\n\nExemples:\n${warnings.slice(0, 3).map(w =>
            `- Transaction #${w.id}: ${w.warnings.join(', ')}`
        ).join('\n')
            }\n\nContinuer l'export ?`;

        return window.confirm(warningMsg);
    }

    // Des erreurs détectées
    if (invalid > 0) {
        const errorMsg = `⚠️ ATTENTION : ${invalid} transaction(s) invalide(s) sur ${total} ne seront PAS exportées.\n\nExemples:\n${errors.slice(0, 3).map(e =>
            `- Transaction #${e.id}: ${e.errors.join(', ')}`
        ).join('\n')
            }\n\nSeules les ${valid} transactions valides seront exportées.\n\nContinuer ?`;

        toast.warn(`${invalid} transaction(s) invalide(s) exclue(s) de l'export`, {
            autoClose: 5000
        });

        return window.confirm(errorMsg);
    }

    // Tout est valide
    return true;
};
